import { Link } from "react-router-dom";
import {
  ArrowRight,
  CircuitBoard,
  Wrench,
  Settings,
  Gauge,
  Activity,
  Cpu,
  CheckCircle2,
} from "lucide-react";
import { CTASection } from "@/components/CTASection";
import { SectionHeading, EyebrowLabel } from "@/components/SectionHeading";
import { IMG } from "@/lib/constants";

const servicesImage = IMG.controlPanelBlue;

const serviceImages = {
  "Electrical Panel Design": IMG.panelMfg,
  "Panel Manufacturing": IMG.workshopA,
  "Installation & Commissioning": IMG.workshopB,
};

const panelImages = {
  MCC: IMG.panelDetail,
  PCC: IMG.industrialPanel,
  APFC: IMG.panel3,
};

const services = [
  {
    num: "01",
    icon: CircuitBoard,
    title: "Electrical Panel Design",
    desc: "SLDs, Layouts & Control Circuits engineered in AutoCAD with full compliance to applicable standards.",
    points: ["Single Line Diagrams", "GA & Layout Drawings", "Control Schematics"],
  },
  {
    num: "02",
    icon: Wrench,
    title: "Panel Manufacturing",
    desc: "Robust fabrication of MCC, PCC, PMCC and APFC panels using premium components and precise workmanship.",
    points: ["MCC / PCC / PMCC", "APFC Panels", "Custom Control Panels"],
  },
  {
    num: "03",
    icon: Settings,
    title: "Installation & Commissioning",
    desc: "On-site installation, wiring verification, load testing and complete commissioning handover.",
    points: ["Site Supervision", "Load & Insulation Testing", "Handover Documentation"],
  },
];

const panels = [
  {
    code: "MCC",
    icon: Cpu,
    title: "MCC Panels",
    desc: "Motor Control Centers built for reliability in heavy-duty industrial environments.",
  },
  {
    code: "PCC",
    icon: Activity,
    title: "PCC Panels",
    desc: "Power Control Centers designed for main incoming feeders and outgoing distribution.",
  },
  {
    code: "APFC",
    icon: Gauge,
    title: "APFC Panels",
    desc: "Automatic Power Factor Correction panels to optimize power quality and reduce losses.",
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <section className="relative bg-navy-950 text-white overflow-hidden border-b-4 border-[#EAB308]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-55"
          style={{ backgroundImage: `url('${servicesImage}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/90 via-navy-950/60 to-navy-950/20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-36">
          <div className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
            <span className="h-px w-10 bg-[#EAB308]" />
            Our Services
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-[1.05] max-w-4xl">
            Electrical Engineering — <span className="text-[#EAB308]">concept to commissioning.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed">
            We offer complete Electrical Panel Solutions — spanning design,
            manufacturing and installation — tailored for manufacturing,
            IT and commercial environments.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32">
          <SectionHeading
            eyebrow="What We Deliver"
            title="Three core capabilities. One engineering promise."
          />

          <div className="mt-16 grid lg:grid-cols-3 gap-8">
            {services.map((s, i) => (
              <article
                key={s.title}
                data-testid={`service-${i}`}
                className="group relative h-[600px] overflow-hidden bg-navy-950 border border-navy-800 rounded-2xl"
              >
                <img
                  src={serviceImages[s.title]}
                  alt={s.title}
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
                />
                
                {/* Gradient Overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/20 to-transparent" />
                
                {/* Top Left Icon */}
                <div className="absolute top-0 left-0 bg-[#EAB308] text-navy-950 p-5 z-20 rounded-br-2xl">
                  <s.icon className="h-7 w-7" strokeWidth={2.5} />
                </div>
                
                {/* Floating Number */}
                <div className="absolute top-6 right-8 text-7xl font-black tracking-tighter text-white/5 select-none transition-all duration-700">
                  {s.num}
                </div>

                {/* Bottom Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8 z-20">
                  <h3 className="text-2xl md:text-3xl font-black tracking-tighter text-white leading-tight uppercase">
                    {s.title}
                  </h3>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Panel types */}
      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <EyebrowLabel>Panel Types</EyebrowLabel>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-zinc-950 leading-[0.98]">
                Panels engineered <br /> for performance.
              </h2>
            </div>
            <p className="max-w-md text-zinc-600 leading-relaxed">
              We build a complete range of LT & MV panels to suit industrial,
              commercial and mission-critical facilities.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {panels.map((p) => (
              <div
                key={p.code}
                className="group relative h-[480px] overflow-hidden bg-white border border-zinc-200 rounded-2xl"
              >
                <img
                  src={panelImages[p.code]}
                  alt={p.title}
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/20 to-transparent" />
                
                <div className="absolute top-6 right-6 bg-navy-950 text-[#EAB308] px-4 py-2 text-xs font-black tracking-[0.3em] uppercase z-20 rounded-md">
                  {p.code}
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-8 z-20">
                  <p.icon className="h-10 w-10 text-[#EAB308]" strokeWidth={1.5} />
                  <h3 className="mt-6 text-2xl md:text-3xl font-black tracking-tighter text-white leading-tight uppercase">
                    {p.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32">
          <SectionHeading
            eyebrow="Process"
            title="How we deliver — in four disciplined steps."
          />
          <div className="mt-14 grid md:grid-cols-4 gap-0 border border-zinc-200">
            {[
              { s: "01", t: "Requirement", d: "Load study, site conditions, applicable standards." },
              { s: "02", t: "Design", d: "SLD, GA, control schematics, BOQ & approvals." },
              { s: "03", t: "Manufacturing", d: "Fabrication, wiring, QA inspection, routine tests." },
              { s: "04", t: "Commissioning", d: "Dispatch, install, test & handover with docs." },
            ].map((p, i) => (
              <div
                key={p.s}
                className={`p-8 md:p-10 ${
                  i !== 3 ? "md:border-r border-zinc-200" : ""
                } ${i < 3 ? "border-b md:border-b-0 border-zinc-200" : ""}`}
              >
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#EAB308]">
                  Step {p.s}
                </div>
                <h4 className="mt-3 text-xl font-black tracking-tight text-zinc-950">
                  {p.t}
                </h4>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                  {p.d}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="services-cta"
              className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#FACC15] text-navy-950 px-6 py-4 font-bold uppercase tracking-wide text-sm border-2 border-navy-950"
            >
              Discuss Your Panel Needs
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
